<template>
  <li class="ticket-card" :class="`ticket-card--color-${color}`">
    <div class="content">
      <span class="subtitle t-caption-small" v-html="nbsp(ticket.subtitle)" />
      <h3 class="title t-h2" v-html="nbsp(ticket.title)" />
      <p
        v-if="ticket.description"
        class="description t-caption"
        v-html="nbsp(ticket.description)"
      />
    </div>
    <p class="price">
      <span class="price__value t-h1" v-html="price" />
      <span class="price__currency t-caption-small">zł</span>
    </p>

    <Arrows class="arrows" />
    <OutlineText :text="outlineText" stroke-width="1.5" class="background" />
    <BaseLink
      :tabindex="isCollapseActive ? 0 : -1"
      class="link"
      :link="ticket.link"
    />
  </li>
</template>

<script setup lang="ts">
import {
  PublicTicket,
  PublicTicketTypeColorEnum
} from '@/service/__generated-api'

const props = withDefaults(
  defineProps<{
    outlineText: string
    ticket: PublicTicket
    color?: PublicTicketTypeColorEnum
  }>(),
  { color: 'orange' }
)

const isCollapseActive = useRequiredInject<boolean>('collapse-active')

const price = computed(() => {
  const [whole, decimal] = props.ticket.price.split('.')

  return decimal === '00' ? whole : props.ticket.price
})
</script>

<style scoped lang="scss">
$card-colors: (
  orange: #f28a1a,
  blue: #6fc3eb,
  pink: #ffc3eb
);

.ticket-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: rem(29px);
  justify-content: space-between;
  min-height: rem(246px);
  padding-block: rem(33px) rem(15px);
  padding-inline: rem(21px);
  overflow: hidden;

  @include media-up(md) {
    padding-block-end: rem(23px - 9px);
    padding-inline: rem(24px);
  }

  @include media-up(xl) {
    min-height: rem(349px);
    padding-block: rem(75px) rem(20px);
  }

  &:hover,
  &:focus-within {
    .arrows :deep(path) {
      animation-name: move;
    }
  }

  @each $key, $value in $card-colors {
    &--color-#{$key} {
      background-color: $value;

      .arrows {
        color: $value;
      }
    }
  }
}

.content {
  flex-grow: 1;
}

.subtitle {
  display: block;
  margin-block-end: rem(3px);
}

.title {
  margin-block-end: rem(15px);
}

.description {
  max-width: rem(246px);

  @include media-up(xl) {
    max-width: rem(220px);
  }
}

.price {
  display: flex;
  gap: rem(7px);

  &__currency {
    margin-block-start: rem(6px);
  }
}

.arrows {
  @include size(rem(24px));
  position: absolute;
  inset: 0 0 auto auto;

  @include media-up(md) {
    @include size(rem(31.5px));
  }

  @include media-up(xl) {
    @include size(rem(54px));
  }
}

.background {
  --el-w: 138;
  --el-h: 62;
  --device-w: 375;
  --margin: -0.11;
  --width: calc(var(--el-w) / var(--device-w) * 100svw);

  position: absolute;
  right: calc(-23 / 335 * 100%);
  bottom: calc(
    var(--width) / (var(--el-w) / var(--el-h)) * (-0.25 - var(--margin))
  );
  width: var(--width);
  transform: rotateZ(13.61deg);
  transform-origin: left;

  @include media-up(md) {
    --el-w: 153;
    --el-h: 90;
    --device-w: 768;
    --margin: -0.12;

    right: calc(-35 / 345 * 100%);
  }

  @include media-up(lg) {
    right: calc(-75 / 384 * 100%);
  }

  @include media-up(xl) {
    --el-w: 257;
    --el-h: 151;
    --device-w: 1440;
    --margin: -0.12;

    right: calc(-95 / 349 * 100%);
  }
}

.link {
  @include focus-visible(-2px);
  position: absolute;
  inset: 0;
  color: transparent;
}
</style>
